import React from 'react';

interface TermsContentProps {
  variant?: 'web' | 'modal';
}

export const TermsContent: React.FC<TermsContentProps> = ({ variant = 'web' }) => {
  const isWeb = variant === 'web';
  
  const containerClass = isWeb 
    ? "bg-white p-6 md:p-8 rounded-lg shadow-sm border" 
    : "";
  
  const sectionSpacing = isWeb ? "space-y-6" : "space-y-4";
  const textSize = isWeb ? "text-muted-foreground" : "text-sm text-muted-foreground";
  const headingSize = isWeb ? "text-xl font-semibold mb-3" : "text-lg font-semibold mb-2";
  const listSpacing = isWeb ? "space-y-1" : "space-y-0.5";

  return (
    <div className={containerClass}>
      {isWeb && (
        <p className="text-muted-foreground mb-6">
          forkast is a free, personal meal planning app (web + iOS) currently in beta testing. By creating an account and using the app, you agree to these Terms. Since the app is still in development, features may change frequently, and some bugs may occur.
        </p>
      )}
      
      <div className={sectionSpacing}>
        <section>
          <h2 className={headingSize}>1. Welcome to forkast (Beta)</h2>
          <p className={textSize}>
            forkast is a free, personal meal planning app (web + iOS) currently in beta testing. By creating an account and using the app, you agree to these Terms. Since the app is still in development, features may change frequently, and some bugs may occur.
          </p>
        </section>

        <section>
          <h2 className={headingSize}>2. Who Can Use forkast</h2>
          <p className={textSize}>
            There are no geographic or age restrictions, but you must use forkast responsibly and in good faith. Account creation requires a valid email address and password.
          </p>
        </section>

        <section>
          <h2 className={headingSize}>3. What We Collect and Store</h2>
          <p className={`${textSize} mb-3`}>
            By using the app, you agree that we may collect and store:
          </p>
          <ul className={`list-disc pl-6 ${textSize} ${listSpacing}`}>
            <li>Your name, email, and password (encrypted)</li>
            <li>Recipes you import, add, or upload (including images)</li>
            <li>Meal plans, notes, and comments you create</li>
            <li>AI-generated wellness assessments based on your data</li>
            <li>Group or "family" account structures you create</li>
          </ul>
          <p className={`${textSize} mt-3`}>
            Your data is not shared publicly. If you join a "family" group, your content may be visible to other group members.
          </p>
        </section>
        
        <section>
          <h2 className={headingSize}>4. Third-Party Services</h2>
          <p className={`${textSize} mb-3`}>
            We use third-party providers to run forkast, including:
          </p>
          <ul className={`list-disc pl-6 ${textSize} ${listSpacing}`}>
            <li>Supabase (hosting/database)</li>
            <li>Vercel & GitHub (app delivery)</li>
            <li>Zyte (for recipe scraping/import)</li>
            <li>OpenAI + Langfuse (for AI-powered meal insights and prompt monitoring)</li>
            <li>Tally (for user feedback collection)</li>
            <li>Resend (for transactional emails)</li>
            <li>Meta Pixel (for understanding user interaction and improving ads)</li>
            <li>Apple Sign-In & Google Sign-In (for account authentication)</li>
          </ul>
          <p className={`${textSize} mt-3`}>
            By using forkast, you agree to the terms of use and privacy practices of these providers.
          </p>
        </section>

        <section>
          <h2 className={headingSize}>5. forkast Is Not a Health or Shopping Advisor</h2>
          <p className={`${textSize} mb-3`}>
            forkast helps you organize meals but is not a dietary, medical, nutritional, or shopping advisor.
          </p>
          <ul className={`list-disc pl-6 ${textSize} ${listSpacing} mb-3`}>
            <li>Recipe content (including ingredients, quantities, and images) is provided by you or imported from third-party sources for personal, private use only. forkast does not verify accuracy or copyright status of imported recipes.</li>
            <li>AI suggestions (including wellness summaries, grocery lists, and recipe suggestions) may be incomplete, inaccurate, or inappropriate for your dietary needs.</li>
          </ul>
          <p className={textSize}>
            You are solely responsible for verifying any meal plans, wellness insights, recipes, and shopping lists generated by the app. forkast assumes no responsibility for decisions made based on its outputs.
          </p>
        </section>

        <section>
          <h2 className={headingSize}>6. Intellectual Property</h2>
          <p className={textSize}>
            You retain ownership of any content you upload or import. We do not sell or publicly display your content. We may use anonymized data to improve forkast.
          </p>
        </section>

        <section>
          <h2 className={headingSize}>7. Account Removal & Data Requests</h2>
          <p className={textSize}>
            You can delete your account and all associated data from account settings. This action is permanent and cannot be undone.
          </p>
        </section>

        <section>
          <h2 className={headingSize}>8. Limitation of Liability</h2>
          <p className={textSize}>
            forkast is provided "as is," without warranties. We are not liable for any issues caused by bugs, recipe errors, missing data, or outcomes related to meal planning — including health issues, missed meals, or indirect consequences.
          </p>
        </section>

        <section>
          <h2 className={headingSize}>9. Changes to These Terms</h2>
          <p className={textSize}>
            These Terms may evolve as forkast develops. Significant updates will be communicated via the app or email.
          </p>
        </section>
      </div>
    </div>
  );
};